import IMqttClient from "../../models/IMqttClient";
import type IMqttDeviceBroadcast from "../../models/IMqttDeviceBroadcast";
import { MqttCommand } from "../../models/mqtt-command";
import { MqttBaseService } from "./MqttBaseService";
import { IMediaPlaybackOptions } from "../../models/IMediaPlaybackOptions";
import {
	MqttDevice,
	MqttMediaSyncConfirm,
	MqttMessageAck,
	MqttPlayAudioLanguage,
	MqttPlayContent,
	MqttPlaySubtitleLanguage,
} from "../../models/MqttModel";
import IDisposable from "../../models/IDisposable";
import { AppLogger } from "../../utils/AppLogger";
import { AsyncStorageKeys } from "../../Types";
import { DeviceType } from "../../enums/DeviceType";

// @injectable()
export default class MqttSenderService extends MqttBaseService implements IDisposable {
	constructor(mqttService: IMqttClient, mqttDeviceBroadcast: IMqttDeviceBroadcast) {
		super(mqttDeviceBroadcast);
		this._mqttService = mqttService;
		this._mqttDeviceBroadcast = mqttDeviceBroadcast;
	}

	public setDeviceBroadcast(deviceInfo?: any) {
		const currentDevice = deviceInfo ?? this._mqttDeviceBroadcast;
		this._mqttDeviceBroadcast = {
			...currentDevice,
			sendOnly: false,
		} as IMqttDeviceBroadcast;
	}

	public setDeviceBroadcastAsSendOnly(deviceInfo?: any) {
		AppLogger.log("[MQTT] Set device broadcast to send only.");
		const currentDevice = deviceInfo ?? this._mqttDeviceBroadcast;
		this._mqttDeviceBroadcast = {
			...currentDevice,
			deviceType: localStorage.getItem(AsyncStorageKeys.deviceType) ?? DeviceType.NotSet,
			sendOnly: true,
		} as IMqttDeviceBroadcast;
	}

	public dispose() {
		this._mqttService = null;
		this._mqttDeviceBroadcast = null;
		return null;
	}

	public disconnectDevice() {
		AppLogger.log("[MQTT] Sent disconnect: ", this._mqttDeviceBroadcast);
		this._mqttService?.publish(this.generateCommand(MqttCommand.DisconnectDevice));
		this._mqttService?.end(false);
	}

	public removeScreen() {
		AppLogger.log("[MQTT] Sent remove-screen: ", this._mqttDeviceBroadcast);
		this._mqttService?.publish(this.generateCommand(MqttCommand.DisconnectDevice));
	}

	public broadcastDevice() {
		AppLogger.log("[MQTT] Sent device-broadcast: ", this._mqttDeviceBroadcast);

		this._mqttService?.publish(this.generateCommand(MqttCommand.DeviceBroadcast));
	}

	// Test command
	public testSendCommandTitleDetails() {
		this._mqttService?.publish(
			this.generateCommandWithBody(MqttCommand.SendFromTitleDetails, {
				arg1: "value1",
				arg2: "value2",
			}),
		);
	}

	public discoverAccountDevices() {
		AppLogger.log("[MQTT] Sent discover-account: ", this._mqttDeviceBroadcast);

		this._mqttService?.publish(this.generateCommand(MqttCommand.DiscoverAccount));
	}

	public sendPingCommand(pingId: number = -1, deviceId: any) {
		this._mqttService?.publish(this.generateCommandWithBody(MqttCommand.Ping, { pingId, deviceId, screenId: deviceId }));
	}

	public sendPingBackCommand(pingId: number, deviceId: any) {
		AppLogger.log("pinging back. Current device: ", { pingId });
		this._mqttService?.publish(this.generateCommandWithBody(MqttCommand.PingBack, { pingId, deviceId, screenId: deviceId }));
	}

	// This is used to synchronize connected devices/screens across all opened screens
	public sendSyncDeviceCommand() {
		//note: once this is triggered, this is still sending/syncing the old device name info because we're not updating `this._mqttDeviceBroadcast`.
		AppLogger.log("device to broadcast to other screens: ", this._mqttDeviceBroadcast);
		this._mqttService?.publish(this.generateCommand(MqttCommand.SyncDevice));
	}

	/**
	 * Broadcasts updated device information such as device name change
	 * @param deviceWithOutdatedName
	 * @param deviceWithUpdatedName
	 */
	public broadcastUpdatedDeviceInfo(deviceWithOutdatedName: IMqttDeviceBroadcast, deviceWithUpdatedName: IMqttDeviceBroadcast) {
		this._mqttService?.publish(
			this.generateCommandWithBody(MqttCommand.BroadcastUpdatedDeviceInfo, {
				deviceWithOutdatedName,
				deviceWithUpdatedName,
			}),
		);
	}

	public sendMediaPlaybackOptionsCommand(options: IMediaPlaybackOptions) {
		this._mqttService?.publish(this.generateCommandWithBody(MqttCommand.MediaPlaybackOptions, options));
	}

	public playMovieCommand(mqttPlayContent: MqttPlayContent) {
		AppLogger.log("[MQTT] Sent play-movie: ", mqttPlayContent);

		this._mqttService?.publish(this.generateCommandWithBody(MqttCommand.PlayMovie, mqttPlayContent));
	}

	public playVamCommand(mqttPlayContent: MqttPlayContent) {
		AppLogger.log("[MQTT] Sent play-vam: ", mqttPlayContent);

		this._mqttService?.publish(this.generateCommandWithBody(MqttCommand.PlayVam, mqttPlayContent));
	}

	/**
	 * mqtt command send for resuming movie
	 * @param mqttRequest
	 */
	public mediaPlayCommand(mqttRequest: any) {
		AppLogger.log("[MQTT] Sent media-play: ", mqttRequest);

		this._mqttService?.publish(this.generateCommandWithBody(MqttCommand.MediaPlay, mqttRequest));
	}

	/**
	 * mqtt command send for pausing movie
	 * @param mqttRequest
	 */
	public mediaPauseCommand(mqttRequest: any) {
		AppLogger.log("[MQTT] Sent media-pause: ", mqttRequest);

		this._mqttService?.publish(this.generateCommandWithBody(MqttCommand.MediaPause, mqttRequest));
	}

	/**
	 * mqtt command send for restart movie
	 * @param mqttRequest
	 */
	public mediaRestartCommand(mqttRequest: any) {
		AppLogger.log("[MQTT] Sent media-restart: ", mqttRequest);

		this._mqttService?.publish(this.generateCommandWithBody(MqttCommand.MediaRestart, mqttRequest));
	}

	/**
	 * mqtt command send for restart movie
	 * @param mqttRequest
	 */
	public mediaStopCommand(mqttRequest: any) {
		AppLogger.log("[MQTT] Sent media-stop: ", mqttRequest);

		this._mqttService?.publish(this.generateCommandWithBody(MqttCommand.MediaStop, mqttRequest));
	}

	/**
	 * mqtt command send for syncing movie
	 * @param mqttRequest
	 */
	public mediaSyncCommand(mqttPlayContent: MqttPlayContent) {
		AppLogger.log("[MQTT] Sent media-sync: ", mqttPlayContent);

		this._mqttService?.publish(this.generateCommandWithBody(MqttCommand.MediaSync, mqttPlayContent));
	}

	/**
	 * mqtt command for player seek
	 * @param position
	 * @param options
	 */
	public mediaSeekCommand(mqttPlayContent: MqttPlayContent) {
		AppLogger.log("[MQTT] Sent media-seek: ", mqttPlayContent);

		this._mqttService?.publish(this.generateCommandWithBody(MqttCommand.MediaSeek, mqttPlayContent));
	}

	/**
	 * mqtt send command for subtitle change
	 * @param mqttPlaySubtitleLanguage
	 */
	public mediaSubtitleCommand(mqttPlaySubtitleLanguage: MqttPlaySubtitleLanguage) {
		AppLogger.log("[MQTT] Sent media-subtitle: ", mqttPlaySubtitleLanguage);

		this._mqttService?.publish(this.generateCommandWithBody(MqttCommand.MediaSubtitle, mqttPlaySubtitleLanguage));
	}

	/**
	 * mqtt send command for audio change
	 * @param mqttMediaSyncConfirm
	 */
	public mediaSyncConfirm(mqttMediaSyncConfirm: MqttMediaSyncConfirm) {
		AppLogger.log("[MQTT] Sent media-sync-confirm: ", mqttMediaSyncConfirm);

		this._mqttService?.publish(this.generateCommandWithBody(MqttCommand.MediaSyncConfirm, mqttMediaSyncConfirm));
	}

	/**
	 * mqtt send command for audio change
	 * @param mqttPlayAudioLanguage
	 */
	public mediaAudioCommand(mqttPlayAudioLanguage: MqttPlayAudioLanguage) {
		AppLogger.log("[MQTT] Sent media-audio: ", mqttPlayAudioLanguage);

		this._mqttService?.publish(this.generateCommandWithBody(MqttCommand.MediaAudio, mqttPlayAudioLanguage));
	}

	/**
	 * mqtt send message acknowledgement
	 * @param mqttMessageAck
	 */
	public messageAcknowledgement(mqttMessageAck: MqttMessageAck) {
		AppLogger.log("[MQTT] Sent message-ack: ", mqttMessageAck);

		this._mqttService?.publish(this.generateCommandWithBody(MqttCommand.MessageAcknowledgement, mqttMessageAck));
	}

	/**
	 * mqtt command when device sync is done
	 * @param device
	 * @param options
	 */
	public SyncDeviceDoneCommand(device: MqttDevice | undefined, options: any) {
		this._mqttService?.publish(
			this.generateCommandWithBody(MqttCommand.SyncDeviceDone, {
				device,
				parentProductId: options?.parentProductId,
				vamId: options?.vamId,
			}),
		);
	}

	public broadcastPositionCommand(mqttPlayContent: any) {
		const message = this.generateCommandWithBody(MqttCommand.MediaPosition, mqttPlayContent);
		AppLogger.log("[MQTT] Sent media-position: ", JSON.parse(message));

		this._mqttService?.publish(message);
	}

	public mediaTrailerCommand(trailerDetails: any) {
		this._mqttService?.publish(
			this.generateCommandWithBody(MqttCommand.PlayTrailer, {
				trailerDetails,
			}),
		);
	}

	public mediaProgressBarCommand(deviceId: any) {
		this._mqttService?.publish(
			this.generateCommandWithBody(MqttCommand.MediaProgressBar, {
				deviceId: deviceId,
			}),
		);
	}
}
